import React, { useEffect, useState, useContext } from 'react';
import { Box, Typography, Paper, LinearProgress, Button, Grid, Avatar, Fade, Chip } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SchoolIcon from '@mui/icons-material/School';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import TourIcon from '@mui/icons-material/Tour';
import InfoIcon from '@mui/icons-material/Info';
import ChatIcon from '@mui/icons-material/Chat';
import Badge from '@mui/material/Badge';

import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import LottieLoading from '../../components/common/LottieLoading';
import { decodeHTMLEntities } from '../../utils/stringUtils';

import { useLearnerTour } from '../../hooks/useLearnerTour';

const LearnerDashboard = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState({ totalTime: 0, completedLessons: 0, completedCourses: 0, coursesStarted: 0, minHours: 0 });
    const [inProgressCourses, setInProgressCourses] = useState<any[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { startTour } = useLearnerTour();

    useEffect(() => {
        const fetchData = async () => {
            const { getUnreadCount } = await import('../../services/chatApi');
            try {
                const [count] = await Promise.all([
                    getUnreadCount(),
                    fetchStats(),
                    fetchInProgressCourses()
                ]);
                setUnreadCount(count);
            } catch (err) {
                console.error("Error fetching dashboard data", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const fetchStats = async () => {
        try {
            const res = await api.get('/api/progress/stats');
            const userRes = await api.get(`/api/users/${user?._id}`);
            setStats({
                ...res.data
            });
        } catch (err) {
            console.error("Error fetching stats", err);
        }
    };

    const fetchInProgressCourses = async () => {
        try {
            const res = await api.get('/api/progress/in-progress');
            setInProgressCourses(res.data);
        } catch (err) {
            console.error("Error fetching in-progress courses", err);
        }
    };

    const formatTime = (seconds: number) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return `${h}h ${m}m`;
    };

    if (loading) return <LottieLoading message="Chargement de votre progression..." animationType="learner" />;

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            p: { xs: 2, md: 4 }
        }}>
            {/* Hero Section with Stats */}
            <Paper
                id="learner-hero"
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                    p: { xs: 3, md: 5 },
                    mb: 5,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
                }}
            >
                <Box sx={{ position: 'absolute', top: -100, right: -50, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', filter: 'blur(40px)' }} />
                <Box sx={{ position: 'absolute', bottom: -50, left: 100, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', filter: 'blur(30px)' }} />

                <Grid container spacing={4} alignItems="center">
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <Avatar
                                src={user?.preferences?.photoUrl}
                                alt={user?.firstName}
                                sx={{
                                    width: 80,
                                    height: 80,
                                    border: '4px solid rgba(255,255,255,0.3)',
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
                                }}
                            />
                            <Box>
                                <Typography variant="h3" sx={{
                                    fontWeight: 800,
                                    fontFamily: 'Quicksand, sans-serif',
                                    textShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                    fontSize: { xs: '2rem', md: '3rem' }
                                }}>
                                    Bonjour, {user?.firstName} !
                                </Typography>
                                <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 500, fontFamily: 'Quicksand, sans-serif', mt: 0.5 }}>
                                    Prêt à relever de nouveaux défis aujourd'hui ?
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                                    {stats.completedLessons === 0 && (
                                        <Button
                                            onClick={startTour}
                                            startIcon={<TourIcon />}
                                            sx={{
                                                bgcolor: 'rgba(255,255,255,0.2)',
                                                color: 'white',
                                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                                backdropFilter: 'blur(10px)',
                                                borderRadius: 2,
                                                px: 2,
                                                textTransform: 'none',
                                                fontWeight: 600
                                            }}
                                        >
                                            Commencer la visite
                                        </Button>
                                    )}
                                    <Button
                                        onClick={() => window.open(`/learner/course/695e37ad6ea14d83b4df83cf`, '_blank')}
                                        startIcon={<InfoIcon />}
                                        sx={{
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                            backdropFilter: 'blur(10px)',
                                            borderRadius: 2,
                                            px: 2,
                                            textTransform: 'none',
                                            fontWeight: 600
                                        }}
                                    >
                                        Découvrir la plateforme
                                    </Button>
                                    <Button
                                        onClick={() => navigate('/learner/chat')}
                                        startIcon={
                                            <Badge badgeContent={unreadCount} color="error" overlap="circular">
                                                <ChatIcon />
                                            </Badge>
                                        }
                                        sx={{
                                            bgcolor: unreadCount > 0 ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.2)',
                                            border: unreadCount > 0 ? '2px solid #ff5252' : 'none',
                                            color: 'white',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                            backdropFilter: 'blur(10px)',
                                            borderRadius: 2,
                                            px: 2,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            animation: unreadCount > 0 ? 'pulse 2s infinite' : 'none',
                                            '@keyframes pulse': {
                                                '0%': { transform: 'scale(1)' },
                                                '50%': { transform: 'scale(1.05)' },
                                                '100%': { transform: 'scale(1)' }
                                            }
                                        }}
                                    >
                                        {unreadCount > 0 ? `${unreadCount} nouveaux messages` : 'Messagerie'}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {(user && user.activeConsecutiveDays && user.activeConsecutiveDays > 0)
                                ? <Chip
                                    icon={<LocalFireDepartmentIcon sx={{ color: '#FFD700 !important' }} />}
                                    label={user.activeConsecutiveDays + " jours de suite"}
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', backdropFilter: 'blur(10px)', fontWeight: 600, border: '1px solid rgba(255,255,255,0.3)' }}
                                />
                                : null
                            }
                            {/* Incoming Function : <Chip
                                icon={<EmojiEventsIcon sx={{ color: '#FFD700 !important' }} />}
                                label="Niveau Intermédiaire"
                                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', backdropFilter: 'blur(10px)', fontWeight: 600, border: '1px solid rgba(255,255,255,0.3)' }}
                            />*/}
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 5 }}>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 2
                        }}>
                            <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: 3, p: 2, textAlign: 'center', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                                <AccessTimeIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                                <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                                    {formatTime(stats.totalTime)}
                                    {stats.minHours > 0 && (
                                        <Typography component="span" sx={{ fontSize: '0.6em', opacity: 0.7, ml: 1 }}>
                                            / {stats.minHours}h
                                        </Typography>
                                    )}
                                </Typography>
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1, opacity: 0.8 }}>Temps total</Typography>
                            </Box>
                            <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: 3, p: 2, textAlign: 'center', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
                                <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>{stats.completedCourses}</Typography>
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1, opacity: 0.8 }}>Cours finis</Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Quick Actions / Continue Learning */}
            <Typography id="courses-section" variant="h5" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif', color: '#2c3e50', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PlayCircleOutlineIcon color="primary" />
                Reprendre l'apprentissage
            </Typography>

            {inProgressCourses.length > 0 ? (
                <Grid container spacing={3}>
                    {inProgressCourses.map((course: any, index: number) => (
                        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={course.courseId}>
                            <Fade in timeout={500 + index * 100}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        background: 'white',
                                        borderRadius: 4,
                                        border: '1px solid rgba(0,0,0,0.05)',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 12px 40px rgba(102, 126, 234, 0.2)',
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: 140,
                                            background: course.imageUrl
                                                ? `url(${course.imageUrl})`
                                                : `linear-gradient(135deg, ${index % 2 === 0 ? '#667eea 0%, #764ba2' : '#2193b0 0%, #6dd5ed'} 100%)`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            position: 'relative',
                                            borderTopLeftRadius: 16,
                                            borderTopRightRadius: 16,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {!course.imageUrl && (
                                            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <SchoolIcon sx={{ fontSize: 60, color: 'rgba(255,255,255,0.3)' }} />
                                            </Box>
                                        )}
                                        <Box sx={{ position: 'absolute', bottom: 10, right: 10 }}>
                                            <Chip
                                                label="En cours"
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(255,255,255,0.9)',
                                                    color: '#1976d2',
                                                    fontWeight: 700,
                                                    fontSize: '0.75rem',
                                                    backdropFilter: 'blur(4px)'
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    <Box sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif', color: '#2c3e50', mb: 1, lineHeight: 1.3, minHeight: 42, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                            {decodeHTMLEntities(course.courseTitle)}
                                        </Typography>

                                        <Box sx={{ mt: 'auto' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                    Progression
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#667eea' }}>
                                                    {Math.round(course.progress)}%
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={course.progress}
                                                sx={{
                                                    height: 6,
                                                    borderRadius: 3,
                                                    mb: 2,
                                                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                                                    '& .MuiLinearProgress-bar': {
                                                        borderRadius: 3,
                                                        background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                                                    }
                                                }}
                                            />
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                endIcon={<ArrowForwardIcon />}
                                                onClick={() => window.open(`/learner/course/${course.courseId}`, '_blank')}
                                                sx={{
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    borderRadius: 3,
                                                    textTransform: 'uppercase',
                                                    fontSize: '0.875rem',
                                                    fontWeight: 700,
                                                    py: 1,
                                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                                    '&:hover': {
                                                        boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                                                    }
                                                }}
                                            >
                                                Reprendre
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Fade>
                        </Grid>
                    ))}

                    {/* View All Card */}
                    <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                        <Fade in timeout={800}>
                            <Paper
                                elevation={0}
                                onClick={() => navigate('/learner/parcours')}
                                sx={{
                                    p: 3,
                                    height: '100%',
                                    minHeight: 200,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'rgba(255,255,255,0.5)',
                                    borderRadius: 4,
                                    border: '2px dashed #e0e0e0',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: '#667eea',
                                        background: 'rgba(102, 126, 234, 0.05)',
                                        transform: 'scale(1.02)'
                                    }
                                }}
                            >
                                <Avatar sx={{ width: 60, height: 60, bgcolor: 'white', color: '#667eea', mb: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                                    <SchoolIcon fontSize="large" />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
                                    Voir tous mes parcours
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Accéder au catalogue complet
                                </Typography>
                            </Paper>
                        </Fade>
                    </Grid>
                </Grid>
            ) : (
                <Paper
                    elevation={0}
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        borderRadius: 4,
                        background: 'white',
                        border: '1px dashed #e0e0e0',
                    }}
                >
                    <Box sx={{ mb: 3 }}>
                        <Avatar sx={{ width: 80, height: 80, bgcolor: '#f5f7fa', color: '#667eea', mx: 'auto' }}>
                            <PlayCircleOutlineIcon sx={{ fontSize: 40 }} />
                        </Avatar>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
                        Prêt à commencer ?
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                        Vous n'avez pas encore commencé de cours. Explorez vos parcours assignés pour débuter votre apprentissage.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/learner/parcours')}
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: 3,
                            fontWeight: 600,
                            px: 4
                        }}
                    >
                        Explorer mes parcours
                    </Button>
                </Paper>
            )}    </Box >
    );
};

export default LearnerDashboard;
